import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/cn';
import type { Drawing } from '@/api/endpoints';

/**
 * 进度表的预览块。管理端和工人端共用（CR-026）。
 *
 * 进度表从「只收图片」放开到「图片或 PDF」之后，这一块就不能再无脑 `<img>` 了：
 * 把 PDF 的签名地址塞进 img 的 src，浏览器给的是一个破图图标，
 * 而工地上看到的就是「进度表坏了」。
 *
 * 两种呈现，都点一下进全屏查看器：
 * - **图片**：原图直接铺开。它的价值就是「每天扫一眼」，多一次点击都是多余的；
 * - **PDF**：一行卡片。**不在这里渲染首页缩略图** —— 那要把一兆多的 pdfjs
 *   拉进图纸列表页，而这一页在工地弱网下是工人最常开的之一。
 *   多一次点击换整页秒开，这笔账划算。
 */
export function SchedulePreview({
  drawing,
  onOpen,
  imageClassName,
}: {
  drawing: Drawing;
  onOpen: () => void;
  /** 图片预览的额外样式，比如管理端限高 */
  imageClassName?: string;
}) {
  const { t } = useTranslation();
  const isPdf = drawing.mimeType === 'application/pdf';

  return (
    <button type="button" onClick={onOpen} className="block w-full text-left">
      {isPdf ? (
        <span className="flex min-h-row items-center gap-3 rounded-md border border-rule bg-surface-2 px-4 py-3">
          <span className="shrink-0 rounded bg-brand-600 px-2 py-0.5 font-mono text-caption font-bold text-white">
            PDF
          </span>
          <span className="min-w-0 flex-1">
            <span className="block truncate text-title">{drawing.title}</span>
            <span className="mt-0.5 block text-caption text-ink-3">{t('schedule.openPdf')}</span>
          </span>
        </span>
      ) : (
        <img
          src={drawing.url}
          alt={t('nav.schedule')}
          className={cn('w-full rounded-md border border-rule', imageClassName)}
        />
      )}
    </button>
  );
}
